import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

import Button from '../../../components/ui/Button';
import FilterPanel from '../../../components/ui/FilterPanel';

const InvoiceFilters = ({
  filters,
  onFiltersChange,
  resultCount = 0,
  className = ''
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [searchQuery, setSearchQuery] = useState(filters?.search || '');
  const todayValue = new Date().toLocaleDateString('en-CA');
  const isTodayUpload = filters?.uploadDate === todayValue;
  const uploadDateRef = useRef(null);

  const openUploadDatePicker = () => {
    const el = uploadDateRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {}
    }
    el.focus();
    el.click();
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'parsed', label: 'Parsed' },
    { value: 'needs_review', label: 'Needs Review' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'exported', label: 'Exported' }
  ];

  const confidenceOptions = [
    { value: '', label: 'All Confidence Levels' },
    { value: '0.9', label: 'High (90%+)' },
    { value: '0.7', label: 'Medium (70%+)' },
    { value: '0.5', label: 'Low (50%+)' },
    { value: '0', label: 'All Levels' }
  ];

  const currencyOptions = [
    { value: '', label: 'All Currencies' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'EUR', label: 'EUR (€)' }
  ];

  const vatOptions = [
    { value: '', label: 'All VAT Types' },
    { value: 'standard', label: 'Standard Rate (20%)' },
    { value: 'reduced', label: 'Reduced Rate (5%)' },
    { value: 'zero', label: 'Zero Rate (0%)' },
    { value: 'mixed', label: 'Mixed Rates' },
    { value: 'none', label: 'No VAT' }
  ];

  const amountRangeOptions = [
    { value: '', label: 'All Amounts' },
    { value: '0-100', label: 'Under £100' },
    { value: '100-500', label: '£100 - £500' },
    { value: '500-1000', label: '£500 - £1,000' },
    { value: '1000-5000', label: '£1,000 - £5,000' },
    { value: '5000+', label: 'Over £5,000' }
  ];

  const filterSchema = [
    {
      key: 'search',
      type: 'search',
      label: 'Search',
      placeholder: 'Search by vendor, invoice #, filename...'
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      options: statusOptions,
      clearable: true
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'Date Range'
    },
    {
      key: 'uploadDate',
      type: 'date',
      label: 'Upload Date'
    },
    {
      key: 'confidence',
      type: 'select',
      label: 'Confidence',
      options: confidenceOptions,
      clearable: true
    },
    {
      key: 'currency',
      type: 'select',
      label: 'Currency',
      options: currencyOptions,
      clearable: true
    },
    {
      key: 'vatType',
      type: 'select',
      label: 'VAT Type',
      options: vatOptions,
      clearable: true
    },
    {
      key: 'amountRange',
      type: 'select',
      label: 'Amount Range',
      options: amountRangeOptions,
      clearable: true,
      quickActions: [
        { label: 'High Value', filters: { amountRange: '5000+' } },
        { label: 'This Month', filters: { dateRange: { from: new Date()?.toISOString()?.slice(0, 7) + '-01', to: new Date()?.toISOString()?.slice(0, 10) } } },
        { label: 'Needs Review', filters: { status: 'needs_review' } }
      ]
    }
  ];

  const handleQuickFilter = (filterType, value) => {
    onFiltersChange({
      ...filters,
      [filterType]: value
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => {
      if (Array.isArray(value)) return value?.length > 0;
      if (typeof value === 'object' && value !== null) {
        return Object.values(value)?.some(v => v !== '' && v !== null);
      }
      return value !== '' && value !== null && value !== undefined;
    })?.length;
  };

  const handleClearSingleFilter = (key) => {
    const currentValue = filters?.[key];

    if (Array.isArray(currentValue)) {
      onFiltersChange({ ...filters, [key]: [] });
      return;
    }

    if (typeof currentValue === 'object' && currentValue !== null) {
      const resetObject = Object.keys(currentValue).reduce((acc, k) => {
        acc[k] = '';
        return acc;
      }, {});
      onFiltersChange({ ...filters, [key]: resetObject });
      return;
    }

    onFiltersChange({ ...filters, [key]: '' });
  };

  const formatFilterValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'object' && value !== null) {
      if ('from' in value || 'to' in value) {
        const fromLabel = value?.from ? new Date(value.from).toLocaleDateString('en-GB') : null;
        const toLabel = value?.to ? new Date(value.to).toLocaleDateString('en-GB') : null;
        if (!fromLabel && !toLabel) return null;
        return [fromLabel, toLabel].filter(Boolean).join(' - ');
      }

      const entries = Object.entries(value).filter(([, v]) => v !== '' && v !== null && v !== undefined);
      if (entries.length === 0) return null;
      return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
    }

    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return value;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Quick Search Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 max-w-2xl">
          <div className="relative">
            <Icon 
              name="Search" 
              size={18} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
            />
            <Input
              type="search"
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e?.target?.value);
                onFiltersChange({ ...filters, search: e?.target?.value });
              }}
              className="pl-10"
            />
          </div>
          <label
            className="relative flex items-center gap-3 px-4 py-2 rounded-full border border-primary/70 bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-smooth cursor-pointer"
            onClick={openUploadDatePicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openUploadDatePicker();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <Icon name="Calendar" size={16} className="text-primary-foreground" />
            <div className="flex flex-col leading-tight">
              <span className="text-[11px] uppercase tracking-wide text-primary-foreground/80">Upload Date</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-primary-foreground">{filters?.uploadDate || 'Any'}</span>
                {filters?.uploadDate && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-primary-foreground bg-primary-foreground/20 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-primary-foreground rounded-full" />
                    {isTodayUpload ? 'Today' : 'Active'}
                  </span>
                )}
              </div>
            </div>
            <div className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground/90">
              Change
            </div>
            <input
              type="date"
              aria-label="Upload date"
              value={filters?.uploadDate || ''}
              onChange={(e) => onFiltersChange({ ...filters, uploadDate: e?.target?.value })}
              ref={uploadDateRef}
              className="absolute inset-0 h-full w-full opacity-0 pointer-events-none"
            />
          </label>
        </div>
        
        <div className="flex items-center space-x-3">
          <span className="text-sm text-muted-foreground">
            {resultCount?.toLocaleString()} invoice{resultCount !== 1 ? 's' : ''}
          </span>
          
          {/* Quick Filter Buttons */}
          <div className="flex items-center space-x-2">
            <Button
              variant={filters?.status === 'needs_review' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('status', filters?.status === 'needs_review' ? '' : 'needs_review')}
              iconName="AlertCircle"
              iconPosition="left"
            >
              Needs Review
            </Button>
            
            <Button
              variant={filters?.status === 'reviewed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('status', filters?.status === 'reviewed' ? '' : 'reviewed')}
              iconName="CheckCircle"
              iconPosition="left"
            >
              Reviewed
            </Button>
            
            <Button
              variant={filters?.confidence === '0.9' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('confidence', filters?.confidence === '0.9' ? '' : '0.9')}
              iconName="TrendingUp"
              iconPosition="left"
            >
              High Confidence
            </Button>
          </div>
        </div>
      </div>
      {/* Advanced Filters Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={onFiltersChange}
        schema={filterSchema}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        showClearAll={getActiveFilterCount() > 0}
        className="bg-background"
      />
      {/* Active Filters Display */}
      {getActiveFilterCount() > 0 && (
        <div className="flex items-center space-x-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          
          {Object.entries(filters)?.map(([key, value]) => {
            const displayValue = formatFilterValue(value);
            if (!displayValue) return null;
            
            return (
              <div
                key={key}
                className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
              >
                <span className="capitalize">{key?.replace(/([A-Z])/g, ' $1')?.toLowerCase()}:</span>
                <span className="font-medium">{displayValue}</span>
                <button
                  onClick={() => handleClearSingleFilter(key)}
                  className="hover:bg-primary/20 rounded p-0.5 transition-smooth"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InvoiceFilters;
